/**
 * Authentication Service
 * Handles all authentication-related API calls
 * @service authService
 */

import { config } from "../config";

// API base URL from config (config.api.baseUrl already includes /api/v1 if needed)
const API_BASE_URL = config.api.baseUrl;

/**
 * Logout user by sending POST request to /logout endpoint
 * @returns Promise with response data
 * @throws Error if logout fails
 */
export const logoutUser = async (): Promise<void> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      credentials: 'same-origin',
    });

    // Check if response is successful (2xx status)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Logout failed with status ${response.status}`
      );
    }

    // Successful logout - response might be empty or contain data
    return await response.json().catch(() => undefined);
  } catch (error) {
    console.error("❌ Помилка logout:", error);
    throw error;
  }
};

/**
 * Login user
 * Mock implementation for testing with user@example.com / 123456
 */
export const loginUser = async (
  email: string,
  password: string
): Promise<any> => {
  try {
    // Mock login для тестування
    if (email === "user@example.com" && password === "123456") {
      // Симулюємо затримку мережі
      await new Promise((resolve) => setTimeout(resolve, 800));

      // Повертаємо mock дані користувача
      return {
        user: {
          id: "uuid-123-test",
          email: "user@example.com",
          businessName: "Іван Петренко",
          role: "master",
          businessType: "salon",
          language: "ua",
          phoneNumber: "+380501234567",
          avatarUrl:
            "https://ui-avatars.com/api/?name=Ivan+Petrenko&background=3498db&color=fff&size=200",
          createdAt: "2024-01-15T10:00:00Z",
          lastLogin: new Date().toISOString(),
          theme: {
            navigationBg: "#2c3e50",
            navigationText: "#ecf0f1",
            navigationAccent: "#3498db",
            headerBg: "#ffffff",
            headerText: "#2c3e50",
          },
        },
        token: "mock-jwt-token-" + Date.now(),
      };
    }

    // Для інших користувачів - реальний API запит
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Login failed with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error("❌ Помилка login:", error);
    throw error;
  }
};

/**
 * Login with Google ID token
 * Tries backend /auth/google, falls back to mock in dev
 */
export const loginWithGoogle = async (idToken: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Google login failed with status ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.warn("⚠️ Google login API недоступний, використовую mock:", error);
    // Mock fallback for development/testing
    return {
      user: {
        id: "uuid-google-test",
        email: "vitafranc93@gmail.com",
        fullName: "Google User",
        role: "client",
        businessType: "salon",
        language: "ua",
        avatarUrl:
          "https://ui-avatars.com/api/?name=Google+User&background=0F9D58&color=fff&size=200",
        phoneNumber: "+380501234567",
        createdAt: "2024-01-15T10:00:00Z",
        lastLogin: new Date().toISOString(),
        theme: {
          navigationBg: "#2c3e50",
          navigationText: "#ecf0f1",
          navigationAccent: "#3498db",
          headerBg: "#ffffff",
          headerText: "#2c3e50",
        },
      },
      token: "mock-google-jwt-token-" + Date.now(),
    };
  }
};

/**
 * Update user language preference
 * @param language - Language code ('en' | 'ua')
 * @returns Promise with response data
 * @throws Error if update fails
 */
export const updateUserLanguage = async (
  language: "en" | "ua"
): Promise<void> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/user/language`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ language }),
    });

    // Check if response is successful (2xx status)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Language update failed with status ${response.status}`
      );
    }

    console.log(`✅ Мову користувача оновлено на: ${language}`);
    return await response.json().catch(() => undefined);
  } catch (error) {
    console.error("❌ Помилка оновлення мови:", error);
    throw error;
  }
};

/**
 * Update user theme colors
 * @param colors - Theme colors object
 * @returns Promise with response data
 * @throws Error if update fails
 */
export const updateUserTheme = async (colors: {
  navigationBg: string;
  navigationText: string;
  navigationAccent: string;
  headerBg: string;
  headerText: string;
}): Promise<void> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/user/theme`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ colors }),
    });

    // Check if response is successful (2xx status)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Theme update failed with status ${response.status}`
      );
    }

    console.log("✅ Тему користувача оновлено");
    return await response.json().catch(() => undefined);
  } catch (error) {
    console.error("❌ Помилка оновлення теми:", error);
    throw error;
  }
};

/**
 * Update user profile
 * @param profileData - Profile data to update
 * @returns Promise with updated user data
 * @throws Error if update fails
 */
export const updateUserProfile = async (profileData: {
  first_name: string;
  last_name: string;
  email?: string;
  phone?: string;
}): Promise<any> => {
  try {
    const token = localStorage.getItem("auth_token");

    const response = await fetch(`${API_BASE_URL}/profile`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(profileData),
    });

    // Check if response is successful (2xx status)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      // Конкретні помилки залежно від статусу
      if (response.status === 409) {
        // Conflict - дублікат даних
        throw new Error(
          errorData.error || "Користувач з такими даними вже існує"
        );
      } else if (response.status === 400) {
        // Bad Request - помилка валідації
        throw new Error(errorData.error || "Неправильні дані");
      } else {
        // Інші помилки
        throw new Error(
          errorData.error || `Помилка оновлення профілю (${response.status})`
        );
      }
    }

    const result = await response.json();
    console.log("✅ Профіль користувача оновлено:", result);
    return result;
  } catch (error) {
    console.error("❌ Помилка оновлення профілю:", error);
    throw error;
  }
};
